import React, { useRef, useState } from 'react';
import {
  Accordion,
  Button,
  ConfirmationDialog,
  DescriptionList,
  Notice,
  Spinner,
  Text,
  Textarea,
} from '@heartinternet/hi-ui';
import type { AccordionItem, DescriptionListItem } from '@heartinternet/hi-ui';
import type { SSLInfoContext } from '../SSLInfoPage';
import type { DecodedCsr, SSLInfoData } from '../types';
import { endpoints, isValidCsr, rpcGet, rpcPost } from '../helpers';

/** Download Certificate — native form GET so the browser saves the zip (mirrors certificate-download.js). */
const DownloadContent: React.FC<{ ctx: SSLInfoContext }> = ({ ctx }) => (
  <div className="hi-ui-ssl-info__section-body">
    <Text variant="body">
      To secure your site that&apos;s hosted elsewhere, download the Zip file and install all of the
      certificates in the Zip file on your hosting server, including any intermediate certificates
      that might be needed for older browsers or servers.
    </Text>
    <form method="GET" action={endpoints.download(ctx.baseUrl, ctx.oid)}>
      <Button variant="primary" type="submit">
        Download certificate
      </Button>
    </form>
  </div>
);

/** Revoke Certificate — confirm → GET /revoke → redirect to the summary (mirrors certificate-revoke.js). */
const RevokeContent: React.FC<{ ctx: SSLInfoContext }> = ({ ctx }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setRevoking(true);
    const res = await rpcGet(ctx.fetchFn, endpoints.revoke(ctx.baseUrl, ctx.oid));
    if (res.error) {
      setRevoking(false);
      setConfirmOpen(false);
      setError(res.error);
      return;
    }
    window.location.href = ctx.sslSummaryUrl;
  };

  return (
    <div className="hi-ui-ssl-info__section-body">
      <Text variant="body">
        If you need to revoke your certificate, you can do so here. Please note that once you revoke
        your certificate, it will no longer be valid and you will need to purchase a new certificate
        to secure your site.
      </Text>
      {error && <Notice variant="error">{error}</Notice>}
      <div className="hi-ui-ssl-info__actions">
        <Button variant="warning" onClick={() => setConfirmOpen(true)}>
          Revoke certificate
        </Button>
      </div>
      <ConfirmationDialog
        isOpen={confirmOpen}
        title="Revoke certificate"
        confirmLabel="Yes"
        cancelLabel="Cancel"
        confirmVariant="warning"
        loading={revoking}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      >
        Are you sure you want to revoke this certificate?
      </ConfirmationDialog>
    </div>
  );
};

/** Reissue Certificate — CSR paste + decode when not installed; confirm → POST /reissue (mirrors certificate-reissue.js). */
const ReissueContent: React.FC<{ info: SSLInfoData; ctx: SSLInfoContext }> = ({ info, ctx }) => {
  const isInstalled = info.is_installed;
  const [csrText, setCsrText] = useState('');
  const [csr, setCsr] = useState<string | null>(null);
  const [csrError, setCsrError] = useState<string | null>(null);
  const [decoding, setDecoding] = useState(false);
  const [decoded, setDecoded] = useState<DecodedCsr | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reissuing, setReissuing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastDecodedRef = useRef<string | null>(null);

  const decodeCsr = async (text: string) => {
    const trimmed = text.trim();
    /* v8 ignore next -- redundant-decode guard; only hit by re-submitting an identical CSR */
    if (trimmed === lastDecodedRef.current) return;
    lastDecodedRef.current = trimmed;
    setDecoding(true);
    const res = await rpcPost<DecodedCsr>(ctx.fetchFn, endpoints.decodecsr(ctx.baseUrl), {
      csr: trimmed,
    });
    setDecoding(false);
    if (res.error || !res.data) {
      setCsrError(res.error ?? 'We could not decode that CSR.');
      setCsr(null);
      setDecoded(null);
      return;
    }
    setCsr(trimmed);
    setDecoded(res.data);
  };

  const handleCsrChange = (text: string) => {
    setCsrText(text);
    setCsrError(null);
    setDecoded(null);
    setCsr(null);
    lastDecodedRef.current = null;
    if (text.trim() === '') return;
    if (!isValidCsr(text)) {
      setCsrError('Please include the full CSR, including the BEGIN and END lines');
      return;
    }
    void decodeCsr(text);
  };

  const handleConfirm = async () => {
    setReissuing(true);
    const res = await rpcPost(ctx.fetchFn, endpoints.reissue(ctx.baseUrl, ctx.oid), { csr });
    setReissuing(false);
    setConfirmOpen(false);
    if (res.error) {
      setError(res.error);
      return;
    }
    ctx.refetch();
  };

  // Not installed → a valid decoded CSR is required before reissuing.
  const canReissue = isInstalled || csr !== null;

  const decodedItems: DescriptionListItem[] = decoded
    ? Object.entries({
        'Common name': decoded.common_name,
        Organisation: decoded.organisation,
        Locality: decoded.locality,
        State: decoded.state,
        Country: decoded.country,
        Postcode: decoded.postcode,
        Email: decoded.email,
      })
        .filter(([, v]) => v)
        .map(([label, v]) => ({ label, value: v as string }))
    : [];

  return (
    <div className="hi-ui-ssl-info__section-body">
      {isInstalled ? (
        <Text variant="body">
          Reissue your certificate if you are concerned that it is no longer secure.
        </Text>
      ) : (
        <>
          <Text variant="body">
            Reissue your certificate if you lose your private key or move your website to a new
            server.
          </Text>
          <Text variant="body">
            To reissue your certificate, you will need to generate a new Certificate Signing Request
            (CSR) and paste it below. Save the private key that&apos;s generated alongside.
          </Text>
          <Text variant="body">
            Your newly reissued certificate will be emailed to you. Install the certificate within
            72 hours of issuance to keep your server secure.
          </Text>
          <label className="hi-ui-ssl-info__field-label" htmlFor="ssl-reissue-csr">
            <Text variant="label" bold>
              CSR
            </Text>
          </label>
          <Textarea
            id="ssl-reissue-csr"
            name="reissue-csr"
            rows={8}
            fullWidth
            state={csrError ? 'error' : 'default'}
            value={csrText}
            placeholder="Paste your whole CSR here, including the -----BEGIN CERTIFICATE REQUEST----- and -----END CERTIFICATE REQUEST----- lines."
            onChange={(e) => handleCsrChange(e.target.value)}
          />
          {csrError && (
            <Text variant="error" inline>
              {csrError}
            </Text>
          )}
          {decoding && (
            <div className="hi-ui-ssl-info__inline-spinner">
              <Spinner size="small" aria-label="Decoding CSR" />
            </div>
          )}
          {decodedItems.length > 0 && <DescriptionList layout="horizontal" items={decodedItems} />}
        </>
      )}

      {error && <Notice variant="error">{error}</Notice>}

      <div className="hi-ui-ssl-info__actions">
        <Button variant="primary" disabled={!canReissue} onClick={() => setConfirmOpen(true)}>
          Reissue certificate
        </Button>
      </div>

      <ConfirmationDialog
        isOpen={confirmOpen}
        title="Reissue certificate"
        confirmLabel="Yes"
        cancelLabel="Cancel"
        loading={reissuing}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      >
        Are you sure you want to reissue this certificate?
      </ConfirmationDialog>
    </div>
  );
};

export interface CertificateActionsProps {
  info: SSLInfoData;
  ctx: SSLInfoContext;
}

/** Active-certificate actions: Download (only when not installed), Revoke, Reissue. */
export const CertificateActions: React.FC<CertificateActionsProps> = ({ info, ctx }) => {
  const items: AccordionItem[] = [];
  if (!info.is_installed) {
    items.push({
      id: 'download',
      label: 'Download certificate',
      content: <DownloadContent ctx={ctx} />,
    });
  }
  items.push({
    id: 'revoke',
    label: 'Revoke certificate',
    content: <RevokeContent ctx={ctx} />,
  });
  items.push({
    id: 'reissue',
    label: 'Reissue certificate',
    content: <ReissueContent info={info} ctx={ctx} />,
  });

  return <Accordion items={items} allowMultiple />;
};
